import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { FIRESTORE_COLLECTIONS } from '@financial-portfolio/shared/types';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark'; // The actual applied theme (never 'system')
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light');
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');
  const [themeLoaded, setThemeLoaded] = useState(false);

  // Load theme from localStorage first (instant), then sync with Firestore in background
  useEffect(() => {
    // Load from localStorage immediately for instant theme application
    const localTheme = localStorage.getItem('canvasestate_theme') as Theme | null;
    if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
      setTheme(localTheme);
    }
    setThemeLoaded(true);

    // Sync with Firestore in background (no loading delay)
    const syncWithFirestore = async () => {
      try {
        const user = auth.currentUser;
        if (user?.email) {
          const userDocRef = doc(db, FIRESTORE_COLLECTIONS.USERS, user.email.toLowerCase());
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            const firestoreTheme = data?.["preferences"]?.["theme"] as Theme;

            // Only update if theme exists and is different from localStorage
            if (firestoreTheme && firestoreTheme !== localTheme) {
              setTheme(firestoreTheme);
              localStorage.setItem('canvasestate_theme', firestoreTheme);
            }
          }
        }
      } catch (error) {
        console.error('Error syncing theme from Firestore:', error);
      }
    };

    syncWithFirestore();
  }, []);

  // Effect to resolve system theme and handle changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const updateResolvedTheme = () => {
      if (theme === 'system') {
        setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
      } else {
        setResolvedTheme(theme);
      }
    };

    updateResolvedTheme();

    const handler = () => updateResolvedTheme();
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme]);

  // Effect to persist preference to localStorage and Firestore
  useEffect(() => {
    if (!themeLoaded) return; // Don't save on initial load

    // Save to localStorage immediately (instant persistence)
    localStorage.setItem('canvasestate_theme', theme);

    // Sync to Firestore in background (no blocking)
    const saveTheme = async () => {
      try {
        const user = auth.currentUser;
        if (user?.email) {
          const userDocRef = doc(db, FIRESTORE_COLLECTIONS.USERS, user.email.toLowerCase());
          const now = new Date().toISOString();

          await setDoc(userDocRef, {
            email: user.email?.toLowerCase(),
            preferences: {
              theme: theme,
              updatedAt: now
            },
            updatedAt: now
          }, { merge: true });
        }
      } catch (error) {
        console.error('Error saving theme to Firestore:', error);
      }
    };

    saveTheme();
  }, [theme, themeLoaded]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
